/**
 * Custom React Hook for Users API
 */

import { useState, useEffect, useCallback } from 'react';
import userService from '../services/userService';
import {
  UserDTO,
  CreateUserRequest,
  UserRole,
  PageResponse,
} from '../types/user.types';

interface UseUsersProps {
  role?: UserRole;
  autoFetch?: boolean;
  initialPageSize?: number;
}

export const useUsers = ({ role, autoFetch = true, initialPageSize = 20 }: UseUsersProps = {}) => {
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [currentUser, setCurrentUser] = useState<UserDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = initialPageSize;

  // Fetch all users with pagination
  const fetchUsers = useCallback(async (page: number = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response: PageResponse<UserDTO> = await userService.getAllUsers(page, pageSize);
      setUsers(response.content || []);
      setTotalElements(response.totalElements || 0);
      setTotalPages(response.totalPages || 0);
      setCurrentPage(response.number || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch users by role
  const fetchUsersByRole = useCallback(async (userRole: string) => {
    setLoading(true);
    setError(null);
    try {
      const usersList = await userService.getUsersByRole(userRole);
      setUsers(usersList);
      setTotalElements(usersList.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users by role');
      console.error('Error fetching users by role:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user by ID
  const fetchUserById = useCallback(async (id: string, silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const user = await userService.getUserById(id);
      setCurrentUser(user);

      // Also add to the main users list if not already there
      setUsers(prev => {
        if (prev.some(u => u.id === user.id || u.keycloakId === user.keycloakId || u.username === user.username)) return prev;
        return [...prev, user];
      });

      return user;
    } catch (err: any) {
      console.error('Error fetching user:', err);
      if (!silent) setError(err.response?.data?.message || 'Failed to fetch user');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Fetch user by username
  const fetchUserByUsername = useCallback(async (username: string) => {
    setLoading(true);
    setError(null);
    try {
      const user = await userService.getUserByUsername(username);
      setCurrentUser(user);
      return user;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch user by username');
      console.error('Error fetching user by username:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new user
  const createUser = useCallback(async (request: CreateUserRequest) => {
    setSaving(true);
    setError(null);
    try {
      const newUser = await userService.createUser(request);
      setUsers((prev) => [newUser, ...prev]);
      setCurrentUser(newUser);
      return newUser;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user');
      console.error('Error creating user:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  // Update user
  const updateUser = useCallback(async (id: string, data: Partial<UserDTO>) => {
    setSaving(true);
    setError(null);
    try {
      const updatedUser = await userService.updateUser(id, data);
      setUsers((prev) =>
        prev.map((user) => (user.id === id ? updatedUser : user))
      );
      if (currentUser?.id === id) {
        setCurrentUser(updatedUser);
      }
      return updatedUser;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
      console.error('Error updating user:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentUser]);

  // Delete user
  const deleteUser = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      await userService.deleteUser(id);
      setUsers((prev) => prev.filter((user) => user.id !== id));
      if (currentUser?.id === id) {
        setCurrentUser(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
      console.error('Error deleting user:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentUser]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      if (role) {
        fetchUsersByRole(role);
      } else {
        fetchUsers(0);
      }
    }
  }, [role, autoFetch, fetchUsers, fetchUsersByRole]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    fetchUsers(page);
  }, [fetchUsers]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      fetchUsers(currentPage + 1);
    }
  }, [currentPage, totalPages, fetchUsers]);

  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      fetchUsers(currentPage - 1);
    }
  }, [currentPage, fetchUsers]);

  return {
    // State
    users,
    currentUser,
    loading,
    saving,
    error,
    totalElements,
    totalPages,
    currentPage,
    pageSize,

    // Actions
    fetchUsers,
    fetchUsersByRole,
    fetchUserById,
    fetchUserByUsername,
    createUser,
    updateUser,
    deleteUser,
    handlePageChange,
    nextPage,
    prevPage,
    setCurrentUser,
    setError,
  };
};
